"use client";

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";

import { cx } from "../../cx";
import styles from "./checkbox.module.css";

export type CheckboxSize = "default";
export type CheckboxVariant = "default" | "overlay";

/** Radix spelled the third state as a value; Base UI spells it as a prop. */
type CheckedValue = boolean | "indeterminate";

const isIndeterminate = (value: unknown): value is "indeterminate" =>
  value === "indeterminate";

type CheckboxProps = Omit<
  CheckboxPrimitive.Root.Props,
  "checked" | "defaultChecked" | "onCheckedChange" | "className"
> & {
  className?: string;
  size?: CheckboxSize;
  variant?: CheckboxVariant;
  checked?: CheckedValue;
  defaultChecked?: CheckedValue;
  onCheckedChange?: (
    checked: CheckedValue,
    eventDetails: CheckboxPrimitive.Root.ChangeEventDetails,
  ) => void;
};

function CheckboxIndicator() {
  return (
    <CheckboxPrimitive.Indicator
      keepMounted
      data-slot="checkbox-indicator"
      className={styles.indicator}
      render={(indicatorProps, state) => (
        <span {...indicatorProps}>
          {state.indeterminate ? (
            <span data-slot="checkbox-dash" className={styles.dash} />
          ) : (
            <span data-slot="checkbox-tick" className={styles.tick} />
          )}
        </span>
      )}
    />
  );
}

function Checkbox({
  className,
  checked,
  defaultChecked,
  indeterminate,
  onCheckedChange,
  size = "default",
  variant = "default",
  ...props
}: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root
      data-tui=""
      data-slot="checkbox"
      data-variant={variant}
      data-size={size}
      checked={isIndeterminate(checked) ? false : checked}
      defaultChecked={isIndeterminate(defaultChecked) ? false : defaultChecked}
      indeterminate={
        indeterminate ??
        (isIndeterminate(checked) || isIndeterminate(defaultChecked))
      }
      onCheckedChange={onCheckedChange}
      className={cx(styles.checkbox, className)}
      {...props}
    >
      <CheckboxIndicator />
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
